import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect, lazy, Suspense } from 'react';
import { Analytics as VercelAnalytics } from '@vercel/analytics/react';
import { AuthProvider } from './context/AuthContext';
import { WordPressProvider, useWordPress } from './context/WordPressContext';
import { WpAdminProvider, useWpAdmin } from './context/WpAdminContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { ToastProvider } from './context/ToastContext';
import ProtectedRoute from './components/ProtectedRoute';
import WpProtectedRoute from './components/WpProtectedRoute';
import AppLayout from './components/layout/AppLayout';
import ErrorBoundary from './components/ErrorBoundary';
import ToastContainer from './components/Toast';
import { WordPressAuthBridge } from './components/WordPressAuthBridge';
import Spinner from './components/ui/Spinner';

// Public pages (loaded eagerly — small)
import Landing from './pages/Landing';
import Welcome from './pages/Welcome';
import Pricing from './pages/Pricing';
import Signup from './pages/Signup';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import Connect from './pages/Connect';

// Protected pages (lazy loaded for performance)
const StoreDashboard = lazy(() => import('./pages/StoreDashboard'));
const Analytics = lazy(() => import('./pages/Analytics'));
const Subscription = lazy(() => import('./pages/Subscription'));
const Usage = lazy(() => import('./pages/Usage'));
const Account = lazy(() => import('./pages/Account'));
const WidgetCustomizer = lazy(() => import('./pages/WidgetCustomizer'));

// WordPress-only pages (lazy loaded, only used in iframe)
const SetupWizard = lazy(() => import('./pages/SetupWizard'));
const WpDashboard = lazy(() => import('./pages/WpDashboard'));
const Products = lazy(() => import('./pages/Products'));
const WpSettings = lazy(() => import('./pages/WpSettings'));
const WpReports = lazy(() => import('./pages/WpReports'));

// Check if running in WordPress iframe
const isInWordPress = window.self !== window.top;

function PageLoader() {
  return (
    <div className="flex items-center justify-center h-64">
      <Spinner size="lg" />
    </div>
  );
}

function RouteListener() {
  const location = useLocation();

  useEffect(() => {
    if (isInWordPress && window.parent) {
      window.parent.postMessage({
        type: 'route-change',
        route: location.pathname
      }, '*');
    }
  }, [location]);

  return null;
}

/** Redirects to /setup when in WP and license is not active. */
function WpHomeRedirect() {
  const { isLicenseActive, isSetupComplete, statusLoading } = useWpAdmin();
  const { wpRestSession } = useWordPress();

  // Wait for status to load before deciding
  if (!wpRestSession || statusLoading) {
    return <PageLoader />;
  }

  if (isLicenseActive && isSetupComplete) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Navigate to="/setup" replace />;
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <CurrencyProvider>
          <ToastProvider>
            <WordPressProvider>
              <WpAdminProvider>
                <Router>
                  <RouteListener />
                  <WordPressAuthBridge>
                  <Suspense fallback={<PageLoader />}>
                  <Routes>
                    {/* Public Routes */}
                    <Route path="/" element={isInWordPress ? <WpHomeRedirect /> : <Landing />} />
                    <Route path="/welcome" element={isInWordPress ? <WpHomeRedirect /> : <Welcome />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/signup" element={<Signup />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/connect" element={<Connect />} />

                    {/* WordPress-only Routes (no Supabase auth needed) */}
                    <Route
                      path="/setup"
                      element={
                        <WpProtectedRoute>
                          <SetupWizard />
                        </WpProtectedRoute>
                      }
                    />
                    <Route
                      path="/products"
                      element={
                        <WpProtectedRoute>
                          <AppLayout>
                            <Products />
                          </AppLayout>
                        </WpProtectedRoute>
                      }
                    />
                    <Route
                      path="/settings"
                      element={
                        <WpProtectedRoute>
                          <AppLayout>
                            <WpSettings />
                          </AppLayout>
                        </WpProtectedRoute>
                      }
                    />
                    <Route
                      path="/wp-reports"
                      element={
                        <WpProtectedRoute>
                          <AppLayout>
                            <WpReports />
                          </AppLayout>
                        </WpProtectedRoute>
                      }
                    />

                    {/* Protected Routes (Supabase auth or WP session) */}
                    <Route
                      path="/dashboard"
                      element={
                        isInWordPress ? (
                          <WpProtectedRoute>
                            <AppLayout>
                              <WpDashboard />
                            </AppLayout>
                          </WpProtectedRoute>
                        ) : (
                          <ProtectedRoute>
                            <AppLayout>
                              <StoreDashboard />
                            </AppLayout>
                          </ProtectedRoute>
                        )
                      }
                    />
                    <Route
                      path="/analytics"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <Analytics />
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/subscription"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <Subscription />
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/usage"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <Usage />
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/account"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <Account />
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/widget-customizer"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <WidgetCustomizer />
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />

                    {/* Catch all */}
                    <Route path="*" element={<Navigate to={isInWordPress ? "/" : "/"} replace />} />
                  </Routes>
                  </Suspense>
                  <ToastContainer />
                  </WordPressAuthBridge>
                </Router>
              </WpAdminProvider>
            </WordPressProvider>
          </ToastProvider>
        </CurrencyProvider>
        <VercelAnalytics />
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
